import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { LocalStorageGateway } from '@core/services/local-storage.gateway'
import { ThemeConfig, ThemeModes, type ThemeMode } from './theme.config'

const STORAGE_KEY = 'theme-mode'

/** Source of truth for the active theme mode and its persistence to storage. */
@injectable()
export class ThemeRepository {
  /**
   * Starts at a default rather than null. §3.2's "PM is null until loaded" rule
   * is about server data, where null is the meaningful "not fetched yet" state —
   * a preference has no such state, it always resolves to something. Seeded from
   * the static list because @inject fields aren't populated during field
   * initialisation; restore() replaces it with the configured default.
   */
  themeModePm: ThemeMode = ThemeModes[ThemeModes.length - 1]

  @inject(ThemeConfig)
  private themeConfig!: ThemeConfig

  @inject(Types.ILocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway

  constructor() {
    makeObservable(this, { themeModePm: observable })
  }

  /**
   * Property-injected deps aren't populated during construction, so the restore
   * can't happen there — ThemePresenter.initialize() calls this at bootstrap.
   */
  restore = action(() => {
    this.themeModePm = this.themeConfig.defaultMode

    const stored = this.localStorageGateway.getValue(STORAGE_KEY)
    // Storage is outside input — anything could be under this key, including a
    // value written by an older version of the app. Unrecognised keeps default.
    if (typeof stored === 'string' && this.themeConfig.isSupported(stored)) {
      this.themeModePm = stored
    }
  })

  setThemeMode = action((mode: ThemeMode) => {
    this.themeModePm = mode
    this.localStorageGateway.setValue(STORAGE_KEY, mode)
  })
}
